import { useState } from "react";
import { ActionMenuItem } from "../../../../components/actionMenu/ActionMenu";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";
import {
  getRecallEntry,
  postRequestRecallAwb,
} from "../../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { RecallClientActionsDataInterface } from "../../../../redux/interfaces/recall.interface";
import {
  canRequestRecallAwb,
  getRecallAwbResultStatus,
} from "./recallClientActionsHelper";

const awbStatusTooltips = {
  pending: "Solicitare AWB in curs. Verifica situatia in TMS",
  sent: "Exista deja o solicitare AWB pentru aceasta farmacie",
  unknown: "Rezultat neconfirmat. Verifica situatia in TMS",
};

function useRecallClientAwbAction({
  client,
}: {
  client: RecallClientActionsDataInterface;
}): ActionMenuItem {
  const dispatch = useAppDispatch();
  const entry = useAppSelector((state) => state.recall.entry);
  const isBatchLoading = useAppSelector(
    (state) => state.recall.clientBatch.isLoading,
  );
  const hasPendingClientActions = useAppSelector(
    (state) => state.recall.pendingClientActionIds.length > 0,
  );
  const hasAttemptedAwb = useAppSelector((state) =>
    state.recall.awbAttemptedClientIds.includes(client.clientPlId),
  );
  const [isRequesting, setIsRequesting] = useState(false);
  const requestsReturnOnly = client.awb?.returnAwb === null;
  const disabledReason = client.notice?.receivedAt
    ? "Documentul a fost deja primit; nu se mai poate solicita AWB."
    : client.awb && getRecallAwbResultStatus(client.awb) === "sent"
    ? "AWB-urile au fost deja generate pentru aceasta farmacie."
    : hasAttemptedAwb
    ? "Solicitarea a fost trimisa. Verifica rezultatul in coloana AWB"
    : isBatchLoading || hasPendingClientActions
    ? "Se proceseaza o actiune pentru farmacii"
    : entry.isLoading
    ? "Se incarca datele"
    : entry.isError
    ? "Actualizeaza datele retragerii inainte de solicitarea AWB-ului"
    : !client.document
    ? "Genereaza documentul de retragere inainte de solicitarea AWB-ului."
    : client.notice?.status !== "registered"
    ? "Solicitarea AWB nu este disponibila in starea curenta a documentului."
    : client.awb && !requestsReturnOnly
    ? awbStatusTooltips[getRecallAwbResultStatus(client.awb)]
    : "";

  const handleRequest = async () => {
    if (
      disabledReason ||
      isRequesting ||
      !canRequestRecallAwb(client, hasAttemptedAwb ? [client.clientPlId] : [])
    )
      return;
    setIsRequesting(true);
    const resultAction = await dispatch(
      postRequestRecallAwb({
        idCampaign: client.idCampaign,
        clientPlId: client.clientPlId,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postRequestRecallAwb) !==
      endpointStatus.pending
    ) {
      // Reincarca si dupa eroare: solicitarea poate fi deja inregistrata in TMS.
      await dispatch(getRecallEntry({ id: client.idCampaign }));
      setIsRequesting(false);
    }
  };

  return {
    id: "request-awb",
    label: requestsReturnOnly ? "Solicita AWB retur" : "Solicita AWB",
    icon: "REQUEST_AWB",
    tooltip:
      disabledReason ||
      (requestsReturnOnly
        ? "Solicita in TMS AWB pentru returul de la farmacie la depozit, dupa confirmare."
        : "Solicita in TMS AWB pentru plicul catre farmacie si returul catre depozit, dupa confirmare."),
    disabled: Boolean(disabledReason) || isRequesting,
    loading: isRequesting,
    withConfirmationModal: {
      modalTitle: requestsReturnOnly ? "Solicita AWB retur" : "Solicita AWB",
      modalSubtitle: `Confirmi solicitarea in TMS a ${
        requestsReturnOnly
          ? "AWB-ului de retur"
          : "AWB-urilor pentru plic si retur"
      } pentru farmacia "${client.pharmacyName}"?`,
      modalLeftButtonText: "Anuleaza",
      modalLeftButtonVariant: "outlined",
      modalRightButtonText: "Trimite in TMS",
      modalRightButtonOnClick: handleRequest,
    },
  };
}

export default useRecallClientAwbAction;
